import React, { useState, useEffect } from "react";
import { Card, ListGroup, Dropdown, Spinner} from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import { formatLocalDate } from "../utils/dateUtils";
import ExpandedIcon from "../assets/microscope_96.png";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchObservationByReference, fetchObservation } from "../services/observationService";
import { useStore } from "react-redux";

function ObservationCard({
    title,
    inContext,
    observationData,
    fhirClient,
    patientId,
    enableSearch = false,
    onObservationChange,
    isReply,
    depth = 6,
}) {
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [modalObservationDetails, setModalObservationDetails] = useState(null);
    const [performerName, setPerformerName] = useState("Loading...");
    const [observationHistory, setObservationHistory] = useState([]);
    const [selectedObservation, setSelectedObservation] = useState(observationData || null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [patient, setPatient] = useState(null);
    const store = useStore();
    const handleModalClose = () => setShowModal(false);

    useEffect(() => {
        if (!observationData || !fhirClient) {
            setError("No observation data or FHIR client available.");
            setSelectedObservation(null);
            setPerformerName("N/A");
            setPatient(null);
            return;
        }

        let isMounted = true;

        const loadAttachmentResources = async () => {
            try {
                //console.log(observationData);
                const obs = await fetchObservation(observationData.id, fhirClient, store);
                //console.log("Fetched Observation:", obs);

                if (!isMounted) return;

                if (!obs || "error" in obs) {
                    setError(`Error loading observation${obs?.error ? ` (code: ${obs.error})` : ""}.`);
                    setSelectedObservation(null);
                    setPerformerName("N/A");
                    setPatient(null);
                    return;
                }

                setError("");
                setSelectedObservation(obs);

                // Performer
                const performerRef = obs.performer?.[0]?.reference;
                if (!performerRef || !performerRef.includes("/") || performerRef.endsWith("/undefined")) {
                    setPerformerName("N/A");
                } else {
                    try {
                        const performerData = await fetchProviderByReference(performerRef, fhirClient, store);
                        setPerformerName(performerData?.name || performerData?.display || "Unknown Performer");
                    } catch {
                        setPerformerName("N/A");
                    }
                }

                // Patient
                const subjectRef = obs.subject?.reference;
                if (!subjectRef || !subjectRef.startsWith("Patient/") || subjectRef === "Patient/undefined") {
                    setPatient(null);
                } else {
                    try {
                        const patientData = await fetchPatientByReference(subjectRef, fhirClient, store);
                        setPatient(patientData || null);
                    } catch {
                        setPatient(null);
                    }
                }
            } catch {
                if (isMounted) {
                    setError("Failed to fetch observation.");
                    setSelectedObservation(null);
                    setPerformerName("N/A");
                    setPatient(null);
                }
            }
        };

        loadAttachmentResources();
        return () => { isMounted = false; };
    }, [observationData, fhirClient]);

    useEffect(() => {
        if (!enableSearch || !patientId || !fhirClient) return;

        const fetchObservationsForPatient = async () => {
            setHistoryLoading(true);
            try {
                const response = await fhirClient.request(`Observation?patient=Patient/${patientId}&_sort=-date&_count=10`);  //TODO enhance underlying service to take more params
                const found = response.entry?.map((e) => e.resource) || [];
                setObservationHistory(found);
            } catch {
                setObservationHistory([]);
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchObservationsForPatient();
    }, [enableSearch, patientId, fhirClient]);

    useEffect(() => {
        if (!isReply && enableSearch) {
            if (observationHistory.length > 0) {
                const firstObs = observationHistory[0];
                setSelectedObservation(firstObs);
                if (onObservationChange) onObservationChange(firstObs);
            } else {
                setSelectedObservation(null);
                if (onObservationChange) onObservationChange(null);
            }
        }
    }, [observationHistory, patientId]);

    const handleModalOpen = async () => {
        if (!selectedObservation?.id) {
            setModalObservationDetails(null);
            setShowModal(true);
            return;
        }

        try {
            const ref = `Observation/${selectedObservation.id}`;
            const details = await fetchObservationByReference(ref, fhirClient, store);
            setModalObservationDetails(details || null);
            setShowModal(true);
        } catch {
            setModalObservationDetails(null);
            setShowModal(true);
        }
    };

    const handleSelectObservation = (obs) => {
        setSelectedObservation(obs);
        if (onObservationChange) onObservationChange(obs);
    };

    const fieldRenderers = [
        <ListGroup.Item className={styles.listItem} key="code"><strong>Code:</strong> {selectedObservation?.code?.text || "N/A"}</ListGroup.Item>,
        <ListGroup.Item className={styles.listItem} key="status"><strong>Status:</strong> {selectedObservation?.status || "N/A"}</ListGroup.Item>,
        <ListGroup.Item className={styles.listItem} key="performer"><strong>Performer:</strong> {performerName}</ListGroup.Item>,
        <ListGroup.Item className={styles.listItem} key="effectiveDate"><strong>Effective Date:</strong> {formatLocalDate(selectedObservation?.effectiveDateTime)}</ListGroup.Item>,
        <ListGroup.Item className={styles.listItem} key="value"><strong>Value:</strong> {selectedObservation?.valueQuantity ? `${selectedObservation.valueQuantity.value} ${selectedObservation.valueQuantity.unit}` : selectedObservation?.valueString || "N/A"}</ListGroup.Item>,
        <ListGroup.Item className={styles.listItem} key="interpretation"><strong>Interpretation:</strong> {selectedObservation?.interpretation?.map((i) => i.text).join("; ") || "N/A"}</ListGroup.Item>,
    ];

    return (
        <>
            <Card className={styles.shrinkCard}>
                <Card.Header
                    className={`d-flex justify-content-between align-items-center ${inContext ? styles.inContextCardHeader : styles.cardHeader}`}
                    onClick={handleModalOpen}
                    style={{ cursor: "pointer" }}
                >
                    {/* Left side: Icon + Title */}
                    <div className="d-flex align-items-center">
                         <img
                            src={ExpandedIcon}
                            alt="Observation Icon"
                            className={styles.fhirIcon}
                            onClick={handleModalOpen}
                        />

                        <h6 className={`${styles.titleText} mb-0`} style={{ pointerEvents: "none" }}>
                            {title?.length > 75
                                ? title.substring(0, 75) + "…"
                                : `Observation: ${title}`}
                        </h6>
                    </div>

                    {/* Right side: Dropdown */}
                    <div
                        className={`d-flex align-items-center ${styles.headerRightFixed}`}
                        onClick={(e) => e.stopPropagation()} // prevent header click from opening modal
                    >
                        {depth > 0 && enableSearch && !isReply && (
                            historyLoading ? (
                                <Spinner
                                    animation="border"
                                    role="status"
                                    size="sm"
                                    className={`me-2 ${styles.noShrink}`}
                                >
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                            ) : (
                                <Dropdown
                                    className={styles.noShrink}
                                    onSelect={(eventKey) =>
                                        handleSelectObservation(
                                            observationHistory.find((o) => o.id === eventKey)
                                        )
                                    }
                                >
                                    <Dropdown.Toggle variant="outline-primary" size="sm">
                                        Select Observation
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className={styles.dropdownMenuScroll}>
                                        {observationHistory.map((obs) => (
                                            <Dropdown.Item key={obs.id} eventKey={obs.id}>
                                                {obs.code?.text ||
                                                    obs.code?.coding?.[0]?.display ||
                                                    "Observation"}{" "}
                                                · {formatLocalDate(obs.effectiveDateTime)}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )
                        )}
                    </div>
                </Card.Header>



                {depth > 0 && (
                    <Card.Body>
                        {error ? <p className={styles.errorText}>{error}</p> : <ListGroup variant="flush">{fieldRenderers.slice(0, depth)}</ListGroup>}
                    </Card.Body>
                )}
            </Card>

            <GenericFHIRViewerModal
                show={showModal}
                onClose={handleModalClose}
                resource={modalObservationDetails}
                //title={selectedObservation?.code?.text || "Observation"}
                //icon={ExpandedIcon}
                fhirClient={fhirClient}
            />
        </>
    );
}

export default ObservationCard;
